import { Card, Chip } from '@heroui/react';
import { MessageSquare, Mic, Sparkles } from 'lucide-react';
import type { TaskAiOutput, VisitTask } from '@/store/visitDetailSlice';
import { formatDateTime } from '@/lib/formatDate';
import { PhotoGallery } from './PhotoGallery';

const TASK_STATUS_LABELS: Record<VisitTask['status'], string> = {
  pending: 'Ожидает',
  submitted: 'Отправлено',
};

interface TaskCardProps {
  task: VisitTask;
}

export function TaskCard({ task }: TaskCardProps) {
  const hasValue = task.textValue !== null || task.booleanValue !== null || task.comment !== null;

  return (
    <Card className="bg-surface">
      <Card.Header className="flex-row items-start justify-between gap-2">
        <div className="flex flex-col gap-1">
          <Card.Title>{task.template.title}</Card.Title>
          {task.template.instruction && (
            <Card.Description>{task.template.instruction}</Card.Description>
          )}
        </div>
        <Chip size="sm" variant="soft" color={task.status === 'submitted' ? 'success' : 'default'}>
          {TASK_STATUS_LABELS[task.status]}
        </Chip>
      </Card.Header>
      <Card.Content className="flex flex-col gap-3">
        {task.booleanValue !== null && (
          <Chip size="sm" variant="soft" color={task.booleanValue ? 'success' : 'danger'}>
            {task.booleanValue ? 'Да' : 'Нет'}
          </Chip>
        )}
        {task.textValue !== null && (
          <p className="rounded-[var(--radius)] bg-surface-secondary px-3 py-2 text-sm text-foreground">
            {task.textValue}
          </p>
        )}
        {task.comment && (
          <div className="flex items-start gap-2 text-sm text-muted">
            <MessageSquare className="mt-0.5 size-4 shrink-0" />
            <span>{task.comment}</span>
          </div>
        )}
        {!hasValue && task.status === 'pending' && (
          <p className="text-sm text-muted">Задача ещё не выполнена</p>
        )}
        <PhotoGallery photos={task.photos} />
        {task.aiOutputs?.map((output, index) => (
          <TaskAiBlock key={index} output={output} />
        ))}
        {task.submittedAt && (
          <span className="text-xs font-medium tracking-wide text-muted uppercase">
            Отправлено: {formatDateTime(task.submittedAt)}
          </span>
        )}
      </Card.Content>
    </Card>
  );
}

function TaskAiBlock({ output }: { output: TaskAiOutput }) {
  return (
    <div className="flex flex-col gap-2 rounded-[var(--radius)] border border-border p-3">
      <div className="flex items-center gap-2">
        <Sparkles className="size-4 text-accent" />
        <span className="text-xs font-semibold tracking-wide text-muted uppercase">AI-анализ</span>
        {output.classification && (
          <Chip size="sm" variant="soft" color="accent">
            {output.classification}
          </Chip>
        )}
      </div>
      {output.answers && output.answers.length > 0 && (
        <ul className="flex flex-col gap-2">
          {output.answers.map((answer, index) => (
            <li
              key={index}
              className="flex flex-col gap-1 rounded-[var(--radius)] bg-surface-secondary px-3 py-2"
            >
              <div className="flex items-start justify-between gap-2">
                <span className="text-sm text-foreground">{answer.question}</span>
                <Chip
                  size="sm"
                  variant="soft"
                  color={answer.answer === 'yes' ? 'success' : 'danger'}
                >
                  {answer.answer === 'yes' ? 'Да' : 'Нет'}
                </Chip>
              </div>
              {answer.comment && <span className="text-xs text-muted">{answer.comment}</span>}
            </li>
          ))}
        </ul>
      )}
      {output.photo_descriptions && output.photo_descriptions.length > 0 && (
        <ul className="flex list-disc flex-col gap-1 pl-5 text-sm text-muted">
          {output.photo_descriptions.map((description, index) => (
            <li key={index}>{description}</li>
          ))}
        </ul>
      )}
      {output.audio_transcription && (
        <div className="flex items-start gap-2 text-sm text-muted">
          <Mic className="mt-0.5 size-4 shrink-0" />
          <span>{output.audio_transcription}</span>
        </div>
      )}
    </div>
  );
}
